import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../axios/index.js";
import { authService, tarifasService } from "../services/index.js";

// Configuração do audience esperado para validação do JWT
const EXPECTED_AUDIENCE = "voltrobotics.com.br";
const EXPECTED_ISSUER = "voltrobotics.com.br";

// Função para validar se o token é válido (não expirado e com audience/issuer corretos)
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const decoded = jwtDecode(token);

    // Validar o campo 'aud' (audience)
    if (!decoded.aud || decoded.aud !== EXPECTED_AUDIENCE) {
      return false;
    }

    // Validar o campo 'iss' (issuer)
    if (!decoded.iss || decoded.iss !== EXPECTED_ISSUER) {
      return false;
    }

    // Validar se o token não está expirado
    const currentTime = Date.now() / 1000;
    if (decoded.exp && decoded.exp < currentTime) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

// Função para decodificar JWT e salvar no localStorage
const decodeAndStoreJWT = (token) => {
  try {
    const decoded = jwtDecode(token);

    // Validar o campo 'aud' (audience)
    if (!decoded.aud || decoded.aud !== EXPECTED_AUDIENCE) {
      console.error(`Token JWT inválido: audience esperada '${EXPECTED_AUDIENCE}', recebida '${decoded.aud}'`);
      return null;
    }

    // Validar o campo 'iss' (issuer)
    if (!decoded.iss || decoded.iss !== EXPECTED_ISSUER) {
      console.error(`Token JWT inválido: issuer esperado '${EXPECTED_ISSUER}', recebido '${decoded.iss}'`);
      return null;
    }

    // Validar se o token não está expirado
    const currentTime = Date.now() / 1000;
    if (decoded.exp && decoded.exp < currentTime) {
      console.error("Token JWT expirado");
      return null;
    }

    // Salva as variáveis específicas no localStorage (usando os campos corretos com maiúsculas)
    if (decoded.UnidadeConsumidoraId) {
      localStorage.setItem("unidadeConsumidoraId", decoded.UnidadeConsumidoraId);
    }
    if (decoded.MedidorId) {
      localStorage.setItem("medidorId", decoded.MedidorId);
    }
    if (decoded.ClienteNome) {
      localStorage.setItem("clienteNome", decoded.ClienteNome);
    }
    if (decoded.DataTitularidade) {
      localStorage.setItem("dataTitularidade", decoded.DataTitularidade);
    }

    return {
      ...decoded,
      // Mapeamento para compatibilidade com o código existente
      unidadeConsumidoraId: decoded.UnidadeConsumidoraId,
      medidorId: decoded.MedidorId,
      clienteNome: decoded.ClienteNome,
      dataTitularidade: decoded.DataTitularidade,
    };
  } catch (error) {
    console.error("Erro ao decodificar JWT:", error);
    return null;
  }
};

// Certifique-se de que as ações retornam o erro corretamente

// Ação para login com sessionToken
export const loginWithSessionToken = createAsyncThunk(
  "auth/loginWithSessionToken",
  async (sessionToken, { rejectWithValue }) => {
    try {
      // Use the correct method from authService
      const response = await authService.autenticar({ "sessionTokenId":sessionToken });

      if (response.success && response.data.token) {
        const decodedToken = decodeAndStoreJWT(response.data.token);

        // Verifica se o token foi decodificado com sucesso
        if (!decodedToken) {
          // Limpa tokens se a decodificação falhou
          localStorage.removeItem("token");
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("access_token_tarifasmp");
          localStorage.removeItem("unidadeConsumidoraId");
          localStorage.removeItem("medidorId");
          localStorage.removeItem("clienteNome");
          localStorage.removeItem("dataTitularidade");
          
          return rejectWithValue("Token JWT inválido ou expirado");
        }

        localStorage.setItem("token", response.data.token);
        localStorage.setItem("access_token_tarifasmp", response.data.token);
        localStorage.setItem("sessionToken", sessionToken);

        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.token}`;

        return {
          ...response.data,
          decodedToken,
          unidadeConsumidoraId: decodedToken?.unidadeConsumidoraId,
          medidorId: decodedToken?.medidorId,
          clienteNome: decodedToken?.clienteNome,
          dataTitularidade: decodedToken?.dataTitularidade,
        };
      }

      return rejectWithValue(response.error || "Falha na autenticação");
    } catch (error) {
      console.error('Erro no loginWithSessionToken:', error);
      
      // Limpa tokens
      localStorage.removeItem("token");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("access_token_tarifasmp");
      localStorage.removeItem("unidadeConsumidoraId");
      localStorage.removeItem("medidorId");
      localStorage.removeItem("clienteNome");
      localStorage.removeItem("dataTitularidade");
      
      let errorMessage = 'Erro na autenticação';
      if (error.response?.status === 401) {
        errorMessage = 'Token de sessão inválido ou expirado';
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados de autenticação inválidos';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Ação para validar token existente
export const validateTokenFromStorage = createAsyncThunk(
  "auth/validateTokenFromStorage",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem("token") || localStorage.getItem("access_token_tarifasmp");

    if (!token) {
      return rejectWithValue("Nenhum token de autenticação encontrado");
    }

    try {
      const decodedToken = decodeAndStoreJWT(token);
      
      if (!decodedToken) {
        // Limpa tokens se a decodificação falhou (token inválido ou expirado)
        localStorage.removeItem("token");
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("access_token_tarifasmp");
        localStorage.removeItem("unidadeConsumidoraId");
        localStorage.removeItem("medidorId");
        localStorage.removeItem("clienteNome");
        localStorage.removeItem("dataTitularidade");
        delete axiosInstance.defaults.headers.common["Authorization"];
        
        return rejectWithValue("Token de autenticação inválido ou expirado");
      }

      // Usa o tarifasService para validar o token fazendo uma requisição de teste
      const dataRef = new Date().toISOString().split('T')[0];
      const response = await tarifasService.getBandeiras(dataRef);

      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;

      return {
        ...response.data,
        decodedToken,
        unidadeConsumidoraId: decodedToken?.unidadeConsumidoraId,
        medidorId: decodedToken?.medidorId,
        clienteNome: decodedToken?.clienteNome,
        dataTitularidade: decodedToken?.dataTitularidade,
      };
    } catch (error) {
      console.error('Erro no validateTokenFromStorage:', error);
      
      // Limpa tokens
      localStorage.removeItem("token");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("access_token_tarifasmp");
      localStorage.removeItem("unidadeConsumidoraId");
      localStorage.removeItem("medidorId");
      localStorage.removeItem("clienteNome");
      localStorage.removeItem("dataTitularidade");
      delete axiosInstance.defaults.headers.common["Authorization"];
      
      let errorMessage = 'Erro na validação do token';
      if (error.response?.status === 401) {
        errorMessage = 'Token inválido ou expirado';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Função para inicializar o estado baseado no token armazenado
const initializeAuthState = () => {
  const token = localStorage.getItem("access_token_tarifasmp");
  const isValid = isTokenValid(token);
  
  // Se o token não for válido, limpa todos os dados relacionados
  if (!isValid && token) {
    localStorage.removeItem("token");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("access_token_tarifasmp");
    localStorage.removeItem("unidadeConsumidoraId");
    localStorage.removeItem("medidorId");
    localStorage.removeItem("clienteNome");
    localStorage.removeItem("dataTitularidade");
  }
  
  return {
    token: isValid ? token : null,
    isAuthenticated: isValid,
    medidorId: isValid ? localStorage.getItem("medidorId") : null,
    unidadeConsumidoraId: isValid ? localStorage.getItem("unidadeConsumidoraId") : null,
    dataTitularidade: isValid ? localStorage.getItem("dataTitularidade") : null,
    clienteNome: isValid ? localStorage.getItem("clienteNome") : null,
    user: isValid ? localStorage.getItem("clienteNome") : null,
  };
};

const loginSlice = createSlice({
  name: "login",
  initialState: {
    ...initializeAuthState(),
    isForbidden: false,
    loading: false,
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.isForbidden = false;
      state.medidorId = null;
      state.unidadeConsumidoraId = null;
      state.dataTitularidade = null;
      state.clienteNome = null;

      // Limpa todos os tokens do localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("access_token_tarifasmp");
      localStorage.removeItem("medidorId");
      localStorage.removeItem("unidadeConsumidoraId");
      localStorage.removeItem("dataTitularidade");
      localStorage.removeItem("clienteNome");

      // Remove header de autorização
      delete axiosInstance.defaults.headers.common["Authorization"];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login com sessionToken
      .addCase(loginWithSessionToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithSessionToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.clienteNome || action.payload.user;
        state.token = action.payload.token;
        state.unidadeConsumidoraId = action.payload.unidadeConsumidoraId;
        state.medidorId = action.payload.medidorId;
        state.clienteNome = action.payload.clienteNome;
        state.dataTitularidade = action.payload.dataTitularidade;
        state.error = null;
      })
      .addCase(loginWithSessionToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.unidadeConsumidoraId = null;
        state.medidorId = null;
        state.clienteNome = null;
        state.dataTitularidade = null;
        state.error = action.payload;
      })
      // Validação de token do storage
      .addCase(validateTokenFromStorage.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateTokenFromStorage.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.clienteNome || action.payload.user || action.payload;
        state.unidadeConsumidoraId = action.payload.unidadeConsumidoraId;
        state.medidorId = action.payload.medidorId;
        state.clienteNome = action.payload.clienteNome;
        state.dataTitularidade = action.payload.dataTitularidade;
      })
      .addCase(validateTokenFromStorage.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.unidadeConsumidoraId = null;
        state.medidorId = null;
        state.clienteNome = null;
        state.dataTitularidade = null;
      });
  },
});

export const { logout, clearError } = loginSlice.actions;
export default loginSlice.reducer;
